import { prefersReducedMotion } from '@theme/utilities';

/**
 * <covaso-scroll-to-buy> — wraps a "Shop now" style call to action.
 *
 * When the hero buy box is on the current page, clicking scrolls it to the
 * middle of the viewport and moves focus to its add-to-cart button instead of
 * navigating. When it is not on the page, the click falls through to normal
 * navigation, so the same markup works in the header on every template.
 *
 * Attributes:
 *   data-target-url  URL to send <button> triggers to when no buy box is found.
 *                    <a> triggers use their own href, so they don't need this.
 *   data-target      CSS selector for the buy box. Defaults to .covaso-hero-form.
 */

const DEFAULT_TARGET = '.covaso-hero-form';

class CovasoScrollToBuy extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#handleClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
  }

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    if (event.defaultPrevented) return;

    // Modifier clicks and middle clicks mean "open elsewhere" — never hijack those.
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const target = event.target;
    if (!(target instanceof Element)) return;

    const trigger = target.closest('a, button');
    if (!trigger || !this.contains(trigger)) return;
    if (trigger instanceof HTMLButtonElement && trigger.disabled) return;

    const buyBox = document.querySelector(this.dataset.target || DEFAULT_TARGET);

    if (!buyBox) {
      // Nothing to scroll to here. An <a> navigates on its own; a <button> needs
      // to be sent to the product page explicitly.
      if (trigger instanceof HTMLButtonElement && this.dataset.targetUrl) {
        window.location.href = this.dataset.targetUrl;
      }
      return;
    }

    event.preventDefault();

    buyBox.scrollIntoView({
      behavior: prefersReducedMotion() ? 'instant' : 'smooth',
      block: 'center',
    });

    // Land keyboard and screen reader users on the same control sighted users are
    // now looking at. preventScroll keeps focus from cancelling the smooth scroll.
    const addToCartButton = buyBox.querySelector('button[name="add"]:not([disabled])');
    if (addToCartButton instanceof HTMLElement) {
      addToCartButton.focus({ preventScroll: true });
    }
  };
}

if (!customElements.get('covaso-scroll-to-buy')) {
  customElements.define('covaso-scroll-to-buy', CovasoScrollToBuy);
}
